<template>
  <div class="chart-wrapper" :class="{ 'dark-theme': theme === 'dark', 'light-theme': theme === 'light' }">
    <div ref="chartContainer" class="chart-container"></div>
    <canvas ref="overlayCanvas" class="overlay-canvas" aria-label="Price chart overlay"></canvas>

  <div v-if="tooltipData" class="tooltip" :style="tooltipStyle">
      <div class="tooltip-content">
        <div>Time: {{ formatTooltipTime(tooltipData.time) }}</div>
        <div>Price: {{ fmt(tooltipData.price) }}</div>
      </div>
    </div>

    
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { createChart, LineType } from 'lightweight-charts'
import { throttle } from 'lodash-es'

const props = defineProps({
  symbol: { type: String, required: true },
  direction: { type: String, default: 'UP' },
  historySeconds: { type: Number, default: 30 },
  priceScale: { type: String, default: 'normal' },
  endTimeSec: { type: Number, default: null },
  entryPrice: { type: Number, default: null },
  interval: { type: String, default: 'tick' },
  theme: { type: String, default: 'dark' }, // New prop for theme
})

const emit = defineEmits(['entry-fixed', 'last-price'])

const chartContainer = ref(null)
const overlayCanvas = ref(null)
const showInfoPanel = ref(true)
const tooltipData = ref(null)
const tooltipStyle = ref({})

let chart, series, resizeObs, rafId, ws
const lastPrice = ref(null)
const _entryPrice = ref(props.entryPrice)
const startedAtSec = ref(null)
let entryLine = null

let targetTime = null
let visibleTo = null
let targetPrice = null
const smoothPrice = ref(null)
let lastFrameTime = null

const RIGHT_OFFSET_BARS = 5 // Increased for better spacing
const DOT_RADIUS = 4
const ANIMATION_SPEED = 1

const pulsePhase = ref(1)
const lastSec = ref(0)

const themeColors = {
  dark: {
    background: 'rgba(15, 23, 42, 0.9)',
    text: '#d1d4dc',
    grid: 'rgba(30, 41, 59, 0.5)',
    line: '#fbbf24',
    upColor: '#22c55e',
    downColor: '#ef4444',
    tooltipBg: 'rgba(15, 23, 42, 0.95)',
  },
  light: {
    background: 'rgba(255, 255, 255, 0.9)',
    text: '#1f2937',
    grid: 'rgba(200, 200, 200, 0.3)',
    line: '#00aaff',
    upColor: '#16a34a',
    downColor: '#dc2626',
    tooltipBg: 'rgba(255, 255, 255, 0.95)',
  },
}

const currentTheme = computed(() => themeColors[props.theme])
const isUp = computed(() => props.direction === 'UP')
const entryColor = computed(() => (isUp.value ? currentTheme.value.upColor : currentTheme.value.downColor))
const priceLabelBg = computed(() => {
  if (lastPrice.value == null || smoothPrice.value == null) return '#3b82f6'
  return smoothPrice.value >= lastPrice.value ? currentTheme.value.upColor : currentTheme.value.downColor
})

const nextCandleCountdown = ref('')

function fmt(x) {
  if (x == null) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(x)
}

function formatRemainingTime() {
  if (!props.endTimeSec) return '—'
  const remaining = Math.max(0, props.endTimeSec - nowSec())
  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function formatTooltipTime(time) {
  return new Date(time * 1000).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: props.interval === 'tick' ? '2-digit' : undefined,
  })
}

function nowSec() {
  return Math.floor(Date.now() / 1000)
}

function wsUrl(sym) {
  const lowerSym = sym.toLowerCase()
  if (props.interval === 'tick') {
    return `wss://stream.binance.com:9443/ws/${lowerSym}@trade`
  } else {
    const binInterval = mapIntervalToBinance(props.interval)
    return `wss://stream.binance.com:9443/ws/${lowerSym}@kline_${binInterval}`
  }
}

function mapIntervalToBinance(i) {
  if (i === 'tick') return '1s'
  if (['5s', '15s', '30s'].includes(i)) return '1s'
  return i
}

function restKlinesUrl(sym, seconds) {
  const limit = Math.max(30, Math.min(1000, seconds))
  const interval = mapIntervalToBinance(props.interval)
  return `https://api.binance.com/api/v3/klines?symbol=${sym}&interval=${interval}&limit=${limit}`
}

function getDynamicPriceFormat(price) {
  if (!price) return { type: 'price', precision: 2, minMove: 0.01 }

  if (price < 0.0001) return { type: 'price', precision: 8, minMove: 0.00000001 }
  if (price < 0.01) return { type: 'price', precision: 6, minMove: 0.000001 }
  if (price < 1) return { type: 'price', precision: 5, minMove: 0.00001 }
  if (price < 100) return { type: 'price', precision: 3, minMove: 0.001 }
  return { type: 'price', precision: 2, minMove: 0.01 }
}

function initChart() {
  chart = createChart(chartContainer.value, {
    layout: {
      background: { color: 'transparent' },
      textColor: currentTheme.value.text,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    watermark: {
    visible: false,   // ✅ logo tamamen kapatılır
  },
    rightPriceScale: {
      borderVisible: false,
      entireTextOnly: false,
      textColor: currentTheme.value.text,
      // ✅ Varsayılan formatter (coin'e göre hane)
      tickMarkFormatter: (price) => {
        const fmt = getDynamicPriceFormat(price)
        return price.toFixed(fmt.precision)
      },
    },
    timeScale: {
      borderVisible: false,
      timeVisible: true,
      secondsVisible: props.interval === 'tick',
      fixRightEdge: true,
      fixLeftEdge: true,
      
      rightOffset: RIGHT_OFFSET_BARS,
      tickMarkFormatter: (time) => {
        const date = new Date(time * 1000)
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: props.interval === 'tick' ? '2-digit' : undefined,
        })
      },
    },
    grid: {
      vertLines: { color: currentTheme.value.grid },
      horzLines: { color: currentTheme.value.grid },
    },
    crosshair: {
      mode: 0,
      vertLine: { visible: false },
      horzLine: { visible: false },
    },
    handleScroll: true,
    handleScale: true,
  })

  if (props.interval === 'tick') {
    series = chart.addAreaSeries({
      lineWidth: 2,
      lineType: LineType.Curved,
      lineColor: currentTheme.value.line,
      topColor: `rgba(${hexToRgb(currentTheme.value.line)}, 0.2)`,
      bottomColor: `rgba(${hexToRgb(currentTheme.value.line)}, 0)`,
      lastValueVisible: false,
      crossHairMarkerVisible: false,
      priceLineVisible: false,
      priceFormat: getDynamicPriceFormat(smoothPrice.value),
    })
  } else {
    series = chart.addCandlestickSeries({
      upColor: currentTheme.value.upColor,
      downColor: currentTheme.value.downColor,
      borderVisible: false,
      wickUpColor: currentTheme.value.upColor,
      wickDownColor: currentTheme.value.downColor,
      priceFormat: getDynamicPriceFormat(smoothPrice.value),
    })
  }

  // Tooltip event
  chart.subscribeCrosshairMove((param) => {
    if (!param.time || !param.point) {
      tooltipData.value = null
      return
    }
    const price = series.coordinateToPrice(param.point.y)
    tooltipData.value = { time: param.time, price }
    tooltipStyle.value = {
      left: `${param.point.x + 10}px`,
      top: `${param.point.y - 40}px`,
    }
  })
}



function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r}, ${g}, ${b}`
}

async function loadHistory() {
  try {
    const resp = await fetch(restKlinesUrl(props.symbol, props.historySeconds))
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`)
    const data = await resp.json()

    let points
    if (props.interval === 'tick') {
      points = data.map((d) => ({
        time: Math.floor(d[0] / 1000),
        value: parseFloat(d[4]),
      }))
    } else {
      points = data.map((d) => ({
        time: Math.floor(d[0] / 1000),
        open: +d[1],
        high: +d[2],
        low: +d[3],
        close: +d[4],
      }))
    }

    if (points.length) {
      series.setData(points)

      const lastT = points.at(-1).time
      chart.timeScale().setVisibleRange({
  from: lastT - (props.historySeconds / 2),
  to: lastT + 5,   // ✅ burada +5 bar sağa boşluk bırakıyor
})
      visibleTo = lastT
      targetTime = lastT
      smoothPrice.value = props.interval === 'tick' ? points.at(-1).value : points.at(-1).close
      targetPrice = smoothPrice.value

      // ✅ priceFormat’ı coin bazlı otomatik güncelle
      if (smoothPrice.value != null) {
        const fmt = getDynamicPriceFormat(smoothPrice.value)
        series.applyOptions({ priceFormat: fmt })
      }

      chart.timeScale().setVisibleRange({
        from: lastT - (props.historySeconds / 5), // yarısı kadar geçmiş göster
        to: lastT + RIGHT_OFFSET_BARS,
      })

      // otomatik boyutlandırma
      chart.timeScale().fitContent()
      chart.priceScale('right').applyOptions({ autoScale: true })
    }
  } catch (e) {
    console.error('Failed to load history:', e)
    emit('error', 'Failed to load historical data')
  }
}


function connectWs() {
  if (ws) ws.close()

  const lowerSym = props.symbol.toLowerCase()

  if (props.interval === 'tick') {
    // ✅ 1s kline → sadece kapanış fiyatı
    ws = new WebSocket(`wss://stream.binance.com:9443/ws/${lowerSym}@kline_1s`)

    ws.onmessage = throttle((ev) => {
      try {
        const m = JSON.parse(ev.data)
        const candle = m.k
        if (!candle) return

        const closePrice = parseFloat(candle.c)
        const closeTime = Math.floor(candle.t / 1000)

        lastPrice.value = smoothPrice.value
        targetPrice = closePrice
        targetTime = closeTime

        emit('last-price', closePrice)

        // İlk giriş fiyatını kaydet
        if (props.endTimeSec && _entryPrice.value == null) {
          _entryPrice.value = closePrice
          startedAtSec.value = nowSec()
          ensureEntryLine()
          emit('entry-fixed', _entryPrice.value)
        }
      } catch (e) {
        console.error('❌ WebSocket tick message error:', e)
      }
    }, 200) // tick için hızlı throttle

  } else {
    // ✅ Candle modu → tam mum update
    ws = new WebSocket(wsUrl(props.symbol))

    ws.onmessage = throttle((ev) => {
      try {
        const m = JSON.parse(ev.data)
        const k = m.k
        if (!k) return

        const candle = {
          time: Math.floor(k.t / 1000),  // mumun başlangıç zamanı
          open: parseFloat(k.o),
          high: parseFloat(k.h),
          low: parseFloat(k.l),
          close: parseFloat(k.c),
        }

        // ⚡ Mum kapanana kadar sürekli güncelle
        series.update(candle)

        if (k.x) {
          // Mum kapandı → son halini sabitle
          // candles.push(candle) // istersen kaydet
        }
      } catch (e) {
        console.error('❌ WebSocket candle message error:', e)
      }
    }, 500) // candle için daha yavaş throttle
  }

  ws.onopen = () => console.log('✅ WebSocket connected')
  ws.onerror = (e) => console.error('❌ WebSocket error:', e)
  ws.onclose = () => setTimeout(connectWs, 5000) // auto-reconnect
}





async function reconnect() {
  lastFrameTime = null
  targetTime = null
  visibleTo = null
  targetPrice = null
  smoothPrice.value = null

  if (series) {
    try {
      chart.removeSeries(series)
    } catch {}
  }

  if (props.interval === 'tick') {
    series = chart.addAreaSeries({
      lineWidth: 2,
      lineType: LineType.Curved,
      lineColor: currentTheme.value.line,
      topColor: `rgba(${hexToRgb(currentTheme.value.line)}, 0.2)`,
      bottomColor: `rgba(${hexToRgb(currentTheme.value.line)}, 0)`,
      lastValueVisible: false,
      crossHairMarkerVisible: false,
      priceLineVisible: false,
    })
    chart.timeScale().applyOptions({
      secondsVisible: true,
      rightOffset: RIGHT_OFFSET_BARS,
    })
  } else {
    series = chart.addCandlestickSeries({
  upColor: currentTheme.value.upColor,
  downColor: currentTheme.value.downColor,
  borderVisible: false,
  wickUpColor: currentTheme.value.upColor,
  wickDownColor: currentTheme.value.downColor,

  

  // ✅ candle modunda da hassas fiyat
  priceFormat: {
    type: 'price',
    precision: 5,   // 5 hane
    minMove: 0.00001,
  },
})

chart.timeScale().applyOptions({
  secondsVisible: false,
  rightOffset: RIGHT_OFFSET_BARS,  // sağa boşluk bırak
})
    chart.timeScale().applyOptions({
      secondsVisible: false,
      rightOffset: RIGHT_OFFSET_BARS,
    })
  }

  await loadHistory()
  connectWs()
}

function ensureEntryLine() {
  if (!_entryPrice.value) {
    if (entryLine) {
      series.removePriceLine(entryLine)
      entryLine = null
    }
    return
  }
  const title = `ENTRY ${fmt(_entryPrice.value)}`
  if (entryLine) series.removePriceLine(entryLine)
  entryLine = series.createPriceLine({
    price: _entryPrice.value,
    color: entryColor.value,
    lineWidth: 1,
    lineStyle: 2,
    axisLabelVisible: true,
    title,
  })
}

function smoothScrollAndPrice(ts) {
  if (!chart || !series || targetTime == null) return
  if (props.interval !== 'tick') return
  if (lastFrameTime == null) lastFrameTime = ts

  const deltaSec = Math.min(0.1, (ts - lastFrameTime) / 1000)
  lastFrameTime = ts

  // 🔹 X ekseni kaydırma (ileri akış)
  const scrollSpeed = 2.0
  const scrollProgress = 1 - Math.exp(-scrollSpeed * deltaSec)
  visibleTo += (targetTime - visibleTo) * scrollProgress

  // 🔹 Fiyat yumuşatma (Y ekseni)
  if (props.interval === 'tick' && smoothPrice.value != null && targetPrice != null) {
    const priceLerpSpeed = 2.0
    const priceProgress = 1 - Math.exp(-priceLerpSpeed * deltaSec * 60)
    smoothPrice.value += (targetPrice - smoothPrice.value) * priceProgress

    // ✅ Tek nokta güncelleme (her frame yeni time EKLEME!)
    series.update({
      time: targetTime,          // hep tick zamanı
      value: smoothPrice.value,  // sadece fiyat akıyor
    })
  }

  // Pulse efekti (saniyede bir)
  const currentSec = Math.floor(ts / 1000)
  if (currentSec !== lastSec.value) {
    pulsePhase.value = 0
    lastSec.value = currentSec
  }
  pulsePhase.value = Math.min(1, pulsePhase.value + deltaSec)
}

function drawOverlay() {
   

  const c = overlayCanvas.value
  if (!c) return
  const ctx = c.getContext('2d')
  const dpr = devicePixelRatio || 1
  ctx.clearRect(0, 0, c.width, c.height)   // <-- her zaman temizle

  if (props.interval !== 'tick') return   // <-- ondan sonra çık
  if (!chart || !series || smoothPrice.value == null) return

  const coordX = chart.timeScale().timeToCoordinate(targetTime)
  const coordY = series.priceToCoordinate(smoothPrice.value)

  // ❌ Mobilde bazen null/0 dönüyor → direkt return
  if (coordX == null || coordY == null) return

  const x = coordX * dpr
  const y = coordY * dpr

  // Nokta
  ctx.beginPath()
  ctx.arc(x, y, DOT_RADIUS * dpr, 0, 2 * Math.PI)
  ctx.fillStyle = currentTheme.value.line
  ctx.fill()

  // Ripple efekti
  if (pulsePhase.value < 1) {
    const rippleRadius = (DOT_RADIUS + 10 * pulsePhase.value) * dpr
    ctx.beginPath()
    ctx.arc(x, y, rippleRadius, 0, 2 * Math.PI)
    ctx.strokeStyle = `rgba(${hexToRgb(currentTheme.value.line)}, ${1 - pulsePhase.value})`
    ctx.lineWidth = 1.5 * dpr
    ctx.stroke()
  }

  // ✅ Dinamik precision
  const fmt = getDynamicPriceFormat(smoothPrice.value)
  const labelText = smoothPrice.value.toFixed(fmt.precision)

  const isMobile = window.innerWidth < 768
    const fontSize = isMobile ? 12 : 12
  // --- Price Label ---
  ctx.font = `${fontSize * dpr}px Inter, sans-serif`
  ctx.textBaseline = 'middle'
  
  const paddingX = 8 * dpr
    const paddingY = 4 * dpr
  const textHeight = fontSize * dpr
  const textWidth = ctx.measureText(labelText).width
  const labelWidth = textWidth + paddingX * 2
   const labelHeight = textHeight + paddingY * 2
  
  const labelX = c.width - labelWidth - 4 // sağ kenara yapışmasın

  // ✅ Y konumunu clamp et (mobilde altta kaymayı engelle)
  const safeY = Math.max(labelHeight / 2 + 4, Math.min(c.height / dpr - labelHeight / 2 - 4, coordY))
  const labelY = Math.round(safeY * dpr) - labelHeight / 2

  // Arkaplan kutusu
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
  ctx.shadowBlur = 4
  ctx.fillStyle = priceLabelBg.value
  ctx.beginPath()
  ctx.roundRect(labelX, labelY, labelWidth, labelHeight, 6)
  ctx.fill()

  // Yazı
  ctx.shadowBlur = 0
  ctx.fillStyle = smoothPrice.value >= lastPrice.value ? "#000000" : "#ffffff"
  ctx.textAlign = 'left'
  ctx.fillText(labelText, labelX + paddingX, Math.round(safeY * dpr))
}




function updateNextCandleCountdown() {
  if (props.interval === 'tick') {
    nextCandleCountdown.value = ''
    return
  }
  const binanceInterval = mapIntervalToBinance(props.interval)
  const msPerCandle = {
    '1m': 60000,
    '3m': 180000,
    '5m': 300000,
    '15m': 900000,
    '30m': 1800000,
    '1h': 3600000,
  }[binanceInterval] || 60000
  const now = Date.now()
  const remaining = msPerCandle - (now % msPerCandle)
  const sec = Math.floor(remaining / 1000)
  const min = Math.floor(sec / 60)
  const secRem = sec % 60
  nextCandleCountdown.value = `${min}:${secRem.toString().padStart(2, '0')}`
}

function resizeAll() {
  const el = chartContainer.value
  if (!el) return
  const { width, height } = el.getBoundingClientRect()

  const c = overlayCanvas.value
  if (c) {
    c.width = Math.floor(width * devicePixelRatio)
    c.height = Math.floor(height * devicePixelRatio)
    c.style.width = `${width}px`
    c.style.height = `${height}px`
  }

  chart.applyOptions({ width, height })
}



function startOverlayLoop() {
  const loop = (ts) => {
    drawOverlay()
    smoothScrollAndPrice(ts)
    rafId = requestAnimationFrame(loop)
  }
  rafId = requestAnimationFrame(loop)
}

function stopOverlayLoop() {
  if (rafId) cancelAnimationFrame(rafId)
}

onMounted(() => {
  initChart()
  loadHistory().then(() => {
    connectWs()
    startOverlayLoop()
    setInterval(updateNextCandleCountdown, 1000)
  })

   if (overlayCanvas.value) {
    overlayCanvas.value.style.zIndex = "0"   // chart'ın altında
    // overlayCanvas.value.style.zIndex = "-1" // tamamen en arkaya da gönderebilirsin
  }

  watch(() => props.symbol, () => reconnect())
  watch(() => props.interval, () => reconnect())
  watch(() => props.priceScale, () => {
    chart.applyOptions({
  rightPriceScale: {
    borderVisible: false,
    entireTextOnly: false,
    tickMarkFormatter: (price) => price.toFixed(4), // istediğin kadar hane
  },
})
  })
  watch(() => props.theme, () => reconnect()) // Reconnect on theme change

  resizeObs = new ResizeObserver(throttle(resizeAll, 100))
  resizeObs.observe(chartContainer.value)
})

onUnmounted(() => {
  stopOverlayLoop()
  if (resizeObs) resizeObs.disconnect()
  if (ws) ws.close()
  if (chart) chart.remove()
})
</script>

<style scoped>
.chart-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;   /* ✅ sabit oran */
  max-height: 500px;      /* opsiyonel */
  min-height: 250px;      /* opsiyonel */
  border-radius: 8px;
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.chart-container {
  width: 100%;
  height: 100%;
}

.overlay-canvas {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.info-panel {
  position: absolute;
  top: 20px;
  left: 20px;
  background: v-bind('currentTheme.background');
  border-radius: 10px;
  padding: 16px;
  color: v-bind('currentTheme.text');
  font-family: 'Inter', sans-serif;
  z-index: 5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(8px);
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
}

.info-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #94a3b8;
  margin-right: 10px;
  font-weight: 500;
}

.value {
  font-weight: 600;
}

.tooltip {
  position: absolute;
  background: v-bind('currentTheme.tooltipBg');
  color: v-bind('currentTheme.text');
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  pointer-events: none;
}

.tooltip-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dark-theme {
  --background: rgba(15, 23, 42, 0.9);
}

.light-theme {
  --background: rgba(255, 255, 255, 0.9);
}

:deep(.tv-lightweight-charts canvas) {
  z-index: 0 !important;
}

:deep(a[href*="tradingview.com"]) {
  display: none !important;
}
</style>